import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiLockClosed, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import {
  PaConfirmModal,
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import {
  deletePlatformRole,
  fetchPlatformRoles,
} from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_ROLES } from "store/services/URL";
import PlatformRoleDrawer from "./PlatformRoleDrawer";
import {
  buildRolesListQuery,
  DEFAULT_ROLE_FILTERS,
  getDeleteBlockReason,
} from "./platformRoles.utils";

const PlatformRolesSettings = () => {
  const { refreshPlatformSession } = usePlatformAdmin();
  const canManage = platformRoleAccess("platform.roles.manage");

  const [filters, setFilters] = useState(DEFAULT_ROLE_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deletingRoleId, setDeletingRoleId] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const loadRoles = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildRolesListQuery(filters, URL_PLATFORM_ADMIN_ROLES);
      const payload = await fetchPlatformRoles(endpoint);
      const roles = payload?.roles;
      setRows(Array.isArray(roles?.data) ? roles.data : []);
      setPagination(getPaginationObj(roles, filters));
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load platform roles");
    } finally {
      setLoading(false);
    }
  }, [canManage, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const openCreateDrawer = () => {
    setSelectedRole(null);
    setDrawerMode("create");
  };

  const openEditDrawer = (role) => {
    setSelectedRole(role);
    setDrawerMode("edit");
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedRole(null);
  };

  const requestDelete = (role) => {
    const blockReason = getDeleteBlockReason(role);
    if (blockReason) {
      toast.error(blockReason);
      return;
    }

    setRoleToDelete(role);
  };

  const handleRolesChanged = async () => {
    await loadRoles();
    await refreshPlatformSession({ silent: false, force: true });
  };

  const confirmDelete = async () => {
    if (!roleToDelete?.id) {
      return;
    }

    setDeletingRoleId(roleToDelete.id);

    try {
      await deletePlatformRole(roleToDelete.id);
      toast.success("Platform role deleted");
      setRoleToDelete(null);
      await handleRolesChanged();
    } catch (err) {
      toast.error(err?.message || "Failed to delete platform role");
    } finally {
      setDeletingRoleId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Role",
        render: (row) => (
          <div className="flex items-start gap-2">
            {row.is_built_in ? (
              <HiLockClosed
                className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                title="Built-in role"
                aria-hidden="true"
              />
            ) : null}
            <div>
              <div className="font-medium text-gray-900">{row.name}</div>
              {row.is_platform_owner ? (
                <div className="text-xs text-indigo-600">Full access</div>
              ) : row.is_built_in ? (
                <div className="text-xs text-gray-500">Built-in</div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "members_count",
        label: "Members",
        render: (row) => row.members_count ?? 0,
      },
      {
        key: "permissions_count",
        label: "Permissions",
        render: (row) => row.permissions_count ?? 0,
      },
      {
        key: "status",
        label: "Type",
        render: (row) => (
          <PaStatusBadge
            label={row.is_built_in ? "Built-in" : "Custom"}
            variant={row.is_built_in ? "neutral" : "info"}
          />
        ),
      },
      {
        key: "actions",
        label: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => {
          const deleteReason = getDeleteBlockReason(row);
          const disableDelete = Boolean(deleteReason) || deletingRoleId === row.id;

          return (
            <div className="inline-flex items-center gap-2">
              {row.is_editable ? (
                <button
                  type="button"
                  onClick={() => openEditDrawer(row)}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                >
                  <HiOutlinePencil className="h-4 w-4" aria-hidden="true" />
                  Edit
                </button>
              ) : (
                <span className="text-xs text-gray-400">Locked</span>
              )}
              <button
                type="button"
                disabled={disableDelete}
                title={deleteReason || undefined}
                onClick={() => requestDelete(row)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
              >
                <HiOutlineTrash className="h-4 w-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [deletingRoleId]
  );

  if (!canManage) {
    return (
      <PaAccessDenied
        title="Platform roles"
        subtitle="Manage platform RBAC"
        description="You need platform.roles.manage to manage platform roles."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Platform roles"
        subtitle="Manage platform roles and permission assignments"
        actions={
          <CustomButton
            label="Create role"
            clickHandler={openCreateDrawer}
            cssClass="bg-indigo-600 hover:bg-indigo-700 text-white"
          />
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search role name"
        onClear={searchInput ? () => {
          setSearchInput("");
          setFilters(DEFAULT_ROLE_FILTERS);
        } : undefined}
        resultCount={pagination.total}
      />

      <PaDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        isLoading={loading}
        emptyState={
          <PaEmptyState title="No platform roles found" message="Create a role to get started" />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <PlatformRoleDrawer
        open={Boolean(drawerMode)}
        onClose={closeDrawer}
        mode={drawerMode || "create"}
        role={selectedRole}
        onSaved={handleRolesChanged}
      />

      <PaConfirmModal
        open={Boolean(roleToDelete)}
        onClose={() => setRoleToDelete(null)}
        title={`Delete role "${roleToDelete?.name || ""}"?`}
        description="This cannot be undone. Staff members assigned to this role will lose its permissions."
        confirmLabel="Delete role"
        destructive
        submitting={Boolean(deletingRoleId)}
        onConfirm={confirmDelete}
      />
    </PlatformAdminLayout>
  );
};

export default PlatformRolesSettings;
